import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Container, Grid, Typography, Link, IconButton, Button } from '@mui/material';
import { Facebook, Twitter, LinkedIn, Instagram } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BRAND, BRAND_GRADIENTS } from '../../../config/brand';

// Stil tanımlamaları
const FooterContainer = styled(Box)(({ theme }) => ({
  background: BRAND_GRADIENTS.reverse, // Açıktan koyuya gradient (BRAND'den)
  color: 'white',
  padding: theme.spacing(6, 0),
  flexShrink: 0,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  fontSize: '1.2rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  textDecoration: 'none',
  display: 'block',
  marginBottom: theme.spacing(1),
  transition: 'color 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    color: 'white',
  },
}));

const socialIconStyles = {
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  margin: '0 4px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'white',
    transform: 'translateY(-2px)',
  },
};

const CopyrightText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(2),
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  textAlign: 'center',
  fontSize: '0.9rem',
  opacity: 0.8,
}));

const LanguageButton = styled(Button)(({ theme }) => ({
  minWidth: '80px',
  height: '36px',
  fontSize: '0.875rem',
  fontWeight: 600,
  borderRadius: '8px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
}));

// Şirket Bilgileri (BRAND'den alınıyor)
const companyInfo = {
  name: BRAND.name,
  address: BRAND.contact.address,
  phone: BRAND.contact.phone,
  email: BRAND.contact.email,
};

// Sosyal medya linkleri (BRAND'den alınıyor)
const socialLinks = [
  { icon: <Facebook />, url: BRAND.social.facebook, name: 'Facebook' },
  { icon: <Twitter />, url: BRAND.social.twitter, name: 'Twitter' },
  { icon: <LinkedIn />, url: BRAND.social.linkedin, name: 'LinkedIn' },
  { icon: <Instagram />, url: BRAND.social.instagram, name: 'Instagram' },
];

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const quickLinks = [
    { label: t('navigation.features'), path: '/features' },
    { label: t('navigation.ecommerce'), path: '/ecommerce' },
    { label: t('navigation.privacy'), path: '/privacy-policy' },
    { label: t('navigation.terms'), path: '/terms-of-service' },
    { label: t('navigation.accounting'), path: '/pre-accounting' },
    { label: t('navigation.contact'), path: '/contact' },
  ];

  return (
    <FooterContainer>
      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Şirket Bilgileri */}
          <Grid item xs={12} md={3}>
            <SectionTitle variant="h6">{companyInfo.name}</SectionTitle>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
              {t('footer.companyDescription')}
            </Typography>
            <Box>
              {socialLinks.map((link) => (
                <IconButton 
                  key={link.name} 
                  component="a"
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={link.name}
                  sx={socialIconStyles}
                >
                  {link.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Hızlı Erişim */}
          <Grid item xs={12} md={3}>
            <SectionTitle variant="h6">{t('footer.quickLinks')}</SectionTitle>
            <Grid container>
              {quickLinks.map((link) => (
                <Grid item xs={6} key={link.path}>
                    <FooterLink onClick={() => navigate(link.path)}>
                        {link.label}
                    </FooterLink>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* İletişim */}
          <Grid item xs={12} md={3}>
            <SectionTitle variant="h6">{t('footer.contact')}</SectionTitle>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              {companyInfo.address}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              {companyInfo.phone}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              <Link href={`mailto:${companyInfo.email}`} sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {companyInfo.email}
              </Link>
            </Typography>
          </Grid>

          {/* Dil Seçimi */}
          <Grid item xs={12} md={3}>
            {(() => {
              // "tr-TR" / "en-US" gibi bölgeli kodlarda da doğru buton vurgulansın
              const isEnActive = (i18n.language || 'tr').toLowerCase().startsWith('en');
              return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 1 }}>
              <LanguageButton
                variant="contained"
                onClick={() => i18n.changeLanguage('tr')}
                sx={{
                  backgroundColor: !isEnActive ? '#ffffff' : 'rgba(255, 255, 255, 0.2)',
                  color: !isEnActive ? BRAND.colors.primary : '#ffffff',
                  minWidth: '60px',
                  height: '32px',
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: !isEnActive ? '#f0f0f0' : 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                TR
              </LanguageButton>
              <LanguageButton
                variant="contained"
                onClick={() => i18n.changeLanguage('en')}
                sx={{
                  backgroundColor: isEnActive ? '#ffffff' : 'rgba(255, 255, 255, 0.2)',
                  color: isEnActive ? BRAND.colors.primary : '#ffffff',
                  minWidth: '60px',
                  height: '32px',
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: isEnActive ? '#f0f0f0' : 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                EN
              </LanguageButton>
            </Box>
              );
            })()}
          </Grid>
        </Grid>
        <CopyrightText>
          © {new Date().getFullYear()} {t('footer.copyright')} 
          <Link href={BRAND.contact.website} target="_blank" rel="noopener noreferrer" sx={{ color: 'white', fontWeight: 'bold', textDecoration: 'none' }}>
             {BRAND.companyName.toUpperCase()}
          </Link>
        </CopyrightText>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
